import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { DEFAULT_MONTH_SPAN } from '../config'
import { themeV5 } from 'theme/v5'

export const FALLBACK_COLOUR = '#546E7A'

const CHART_COLOUR_PALETTE = [
  themeV5.palette.accent.teal,
  themeV5.palette.accent.blue,
  themeV5.palette.accent.sky,
  FALLBACK_COLOUR,
]

dayjs.extend(customParseFormat)
export const BAR_WIDTH = 48
export const BAR_GAP = 48
export const BAR_PERCENTAGE = BAR_WIDTH / (BAR_WIDTH + BAR_GAP)
export const MIN_CHART_WIDTH = 600
export const DOWNLOAD_COLUMNS = [
  'agreementNo',
  'customerName',
  'vehicleRegistration',
  'vehicleDescription',
  'make',
  'model',
  'fuelType',
  'vehicleType',
  'co2Emission',
  'monthYear',
  'monthlyEmission'
]

export const MONTHS = [
  { key: 1, label: 'Jan' },
  { key: 2, label: 'Feb' },
  { key: 3, label: 'Mar' },
  { key: 4, label: 'Apr' },
  { key: 5, label: 'May' },
  { key: 6, label: 'Jun' },
  { key: 7, label: 'Jul' },
  { key: 8, label: 'Aug' },
  { key: 9, label: 'Sep' },
  { key: 10, label: 'Oct' },
  { key: 11, label: 'Nov' },
  { key: 12, label: 'Dec' }
]

export const ALL_MONTH_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

export const filterSelectSx = {
  height: 40,
  backgroundColor: '#F5F6F8',
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#DADDE3'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#B8BCC6'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#1976d2'
  }
}

export const menuPaperSx = {
  mt: 1,
  borderRadius: '12px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
  overflow: 'hidden'
}

export const menuListSx = {
  p: 0,
  '& .MuiMenuItem-root': {
    borderBottom: 'none !important'
  },
  '& .MuiMenuItem-root::before, & .MuiMenuItem-root::after': {
    display: 'none !important'
  },
  '& .MuiDivider-root': {
    display: 'none'
  }
}

export const menuItemSx = {
  py: 1.3,
  my: 0.5,
  '&:hover': {
    backgroundColor: '#F5F7FA'
  },
  '&.Mui-selected': {
    backgroundColor: '#F1F4F7',
    color: '#1C1F23'
  },
  '&.Mui-selected:hover': {
    backgroundColor: '#E9EEF3'
  }
}

export const checkboxSx = {
  p: 0,
  mr: 2,
  color: '#C5CBD3',
  '&.Mui-checked': {
    color: '#8CAEB8'
  }
}

export const filterLabelSx = {
  mb: '6px',
  display: 'block',
  color: 'var(--Neutral-900, #111827)',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '16px'
}

export const createInitialFilters = () => {
  const end = dayjs().add(DEFAULT_MONTH_SPAN, 'month')
    .endOf('month')
  const start = dayjs().subtract(DEFAULT_MONTH_SPAN * 2, 'month')
    .startOf('month')
  return {
    fromMonth: start.format('YYYY-MM-DD'),
    toMonth: end.format('YYYY-MM-DD'),
    periods: [],
    vehicleTypes: []
  }
}

export const formatMonthParam = (value, endOfMonth = false) =>
  value
    ? endOfMonth
      ? dayjs(value).endOf('month')
        .format('YYYY-MM-DD')
      : dayjs(value).startOf('month')
        .format('YYYY-MM-DD')
    : undefined

const asNumber = value => {
  const numeric = Number(value)
  return Number.isNaN(numeric) ? 0 : numeric
}

const normaliseTotalsKey = value => (value ?? '').toString().trim()
  .toLowerCase()

const getMonthlyTotalForType = (totals, key) => {
  if (!totals || !key) {
    return 0
  }

  if (Object.prototype.hasOwnProperty.call(totals, key)) {
    return asNumber(totals[key])
  }

  const targetKey = normaliseTotalsKey(key)
  if (!targetKey) {
    return 0
  }

  const match = Object.entries(totals).find(
    ([currentKey]) => normaliseTotalsKey(currentKey) === targetKey
  )
  return match ? asNumber(match[1]) : 0
}

export const buildMonthRanges = (years, months) => {
  if (!Array.isArray(years) || !years.length) {
    return []
  }
  if (!Array.isArray(months) || !months.length) {
    return []
  }

  const seen = new Set()
  const dates = []

  const sortedYears = Array.from(
    new Set(years.filter(year => typeof year === 'number'))
  ).sort((a, b) => a - b)
  const sortedMonths = Array.from(
    new Set(
      months.filter(monthKey => typeof monthKey === 'number' && monthKey >= 1 && monthKey <= 12)
    )
  ).sort((a, b) => a - b)

  sortedYears.forEach(year => {
    sortedMonths.forEach(monthKey => {
      const key = `${year}-${monthKey}`
      if (seen.has(key)) {
        return
      }
      seen.add(key)
      const paddedMonth = monthKey.toString().padStart(2, '0')
      dates.push(dayjs(`${year}-${paddedMonth}-01`).startOf('month'))
    })
  })

  if (!dates.length) {
    return []
  }

  dates.sort((a, b) => a.valueOf() - b.valueOf())

  const ranges = []
  let currentRange = { from: dates[0], to: dates[0] }

  for (let index = 1; index < dates.length; index += 1) {
    const date = dates[index]
    if (date.diff(currentRange.to, 'month') === 1) {
      currentRange = { ...currentRange, to: date }
    } else if (date.diff(currentRange.to, 'month') > 1) {
      ranges.push(currentRange)
      currentRange = { from: date, to: date }
    }
  }

  ranges.push(currentRange)

  return ranges
}

export const createPeriodLabel = (from, to) => {
  if (from.isSame(to, 'month')) {
    return from.format('MMM YYYY')
  }

  return `${from.format('MMM YYYY')} \u2013 ${to.format('MMM YYYY')}`
}

export const ensureVehicleTypeColours = types =>
  types.map((type, index) => {
    const colour =
      type.color ||
      CHART_COLOUR_PALETTE[index % CHART_COLOUR_PALETTE.length] ||
      FALLBACK_COLOUR
    return { ...type, color: colour }
  })

export const deriveDefaultVehicleTypes = () => []

export const buildChartData = (report, vehicleTypes, options = {}) => {
  const { autoWindow = false, windowSize = DEFAULT_MONTH_SPAN } = options
  let months = []
  if (Array.isArray(report?.periods) && report.periods.length > 0) {
    months = report.periods.flatMap(period => {
      const items = Array.isArray(period.months) ? period.months : []
      return items.map(item => ({
        ...item,
        periodLabel: period.label || `${period.fromMonth} \u2013 ${period.toMonth}`
      }))
    })
  } else if (Array.isArray(report?.months)) {
    months = report.months
  }
  if (!months.length) {
    return null
  }

  const parseMonthFromItem = (item) => {
    if (!item) {
      return null
    }
    if (item.fromMonth) {
      return dayjs(item.fromMonth).startOf('month')
    }
    if (item.monthYear) {
      return dayjs(item.monthYear).startOf('month')
    }
    if (item.month && item.year) {
      return dayjs(`${item.year}-${String(item.month).padStart(2, '0')}-01`).startOf('month')
    }

    // Handle item.month as a full date string (e.g. "2024-03-01T00:00:00")
    if (item.month) {
      const d = dayjs(item.month)
      if (d.isValid()) {
        return d.startOf('month')
      }
    }

    if (item.label) {
      const byMonthYear = dayjs(item.label, 'MMM YYYY', true)
      if (byMonthYear.isValid()) {
        return byMonthYear.startOf('month')
      }

      // Handle "YYYY-MM" format labels (e.g. "2024-03")
      const byYearMonth = dayjs(item.label, 'YYYY-MM', true)
      if (byYearMonth.isValid()) {
        return byYearMonth.startOf('month')
      }

      const byMonth = dayjs(item.label, 'MMM', true)
      if (byMonth.isValid() && item.periodLabel) {
        const yearMatch = (item.periodLabel.match(/(19|20)\d{2}/g) || [])
        if (yearMatch.length) {
          const year = yearMatch[0]
          const d = dayjs(`${item.label} ${year}`, 'MMM YYYY', true)
          if (d.isValid()) {
            return d.startOf('month')
          }
        }
      }
    }

    return null
  }

  const mapped = months.map(item => ({ item, date: parseMonthFromItem(item) }))
  const validDates = mapped.filter(m => m.date && m.date.isValid())

  let continuousMonths = months

  if (validDates.length) {
    const map = new Map()
    validDates.forEach(({ item, date }) => {
      const normalized = { ...item, label: date.format('MMM YYYY') }
      map.set(date.format('YYYY-MM'), { item: normalized, date })
    })

    const sorted = Array.from(map.values()).sort((a, b) => a.date.valueOf() - b.date.valueOf())
    continuousMonths = sorted.map(v => v.item)

    if (autoWindow) {
      const firstWithData = continuousMonths.findIndex(item => {
        if (!item?.totals) {
          return false
        }
        return Object.values(item.totals).some(v => Number(v) > 0)
      })

      if (firstWithData > 0) {
        continuousMonths = continuousMonths.slice(firstWithData)
      }

      if (continuousMonths.length > windowSize) {
        continuousMonths = continuousMonths.slice(0, windowSize)
      } else if (continuousMonths.length < windowSize && continuousMonths.length > 0) {
        const lastItem = continuousMonths[continuousMonths.length - 1]
        let lastDate = parseMonthFromItem(lastItem)
        if (lastDate && lastDate.isValid()) {
          const needed = windowSize - continuousMonths.length
          for (let i = 1; i <= needed; i++) {
            const next = lastDate.add(i, 'month')
            continuousMonths.push({ label: next.format('MMM YYYY'), totals: {} })
          }
        }
      }
    }
  }

  const labels = continuousMonths.map(item => item.label)

  const datasets = vehicleTypes.map(type => ({
    label: type.label,
    backgroundColor: type.color || FALLBACK_COLOUR,
    data: continuousMonths.map(item => getMonthlyTotalForType(item?.totals, type.key)),
    stack: 'emissions',
    borderWidth: 0,
    barThickness: BAR_WIDTH,
    maxBarThickness: BAR_WIDTH,
    categoryPercentage: 1,
    barPercentage: BAR_PERCENTAGE
  }))

  return { labels, datasets }
}

export const extractAvailableDates = report => {
  const yearMonths = new Map()

  const processItem = item => {
    if (!item) {
      return
    }
    let date = null
    if (item.fromMonth) {
      date = dayjs(item.fromMonth)
    } else if (item.monthYear) {
      date = dayjs(item.monthYear)
    } else if (item.month && item.year) {
      date = dayjs(`${item.year}-${String(item.month).padStart(2, '0')}-01`)
    } else if (item.label) {
      const parsed = dayjs(item.label, 'MMM YYYY', true)
      if (parsed.isValid()) {
        date = parsed
      }
    }
    if (date && date.isValid()) {
      const year = date.year()
      if (!yearMonths.has(year)) {
        yearMonths.set(year, new Set())
      }
      yearMonths.get(year).add(date.month() + 1)
    }
  }

  if (Array.isArray(report?.periods)) {
    report.periods.forEach(period => {
      if (Array.isArray(period.months)) {
        period.months.forEach(processItem)
      }
    })
  }
  if (Array.isArray(report?.months)) {
    report.months.forEach(processItem)
  }

  const years = Array.from(yearMonths.keys()).sort((a, b) => b - a)
  const monthsByYear = {}
  yearMonths.forEach((months, year) => {
    monthsByYear[year] = Array.from(months).sort((a, b) => a - b)
  })

  return { years, monthsByYear }
}

export const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  layout: { padding: 0 },
  scales: {
    x: {
      stacked: true,
      offset: false,
      grid: { display: false },
      ticks: { autoSkip: false }
    },
    y: {
      stacked: true,
      beginAtZero: true,
      grid: { color: '#E6EBF0' },
      ticks: { callback: value => `${value}` }
    }
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: context => {
          const datasetLabel = context.dataset?.label
            ? `${context.dataset.label}: `
            : ''
          const value = Number(context.raw || 0).toFixed(2)
          return `${datasetLabel}${value} t`
        }
      }
    }
  }
}
